import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useFetch, useDelete, usePost, notify } from '../../../../components/component-items/helpers'
import { globalState } from '../../../../store'

// Components
import { OrderOptions } from '../../../orders/order/styles'
import { ShippingWorkflow } from '../shipping-workflow'
import { PrePackWorkflow } from '../shipping-workflow/pre-pack-workflow'
import GlobalStyles from '../../../../components/component-items/styles'
import CardLoading from '../../../../components/component-items/loading-popover'

// Cards
import { StatusBar } from '../status-bar'
import { OrderItemsCard } from './order-items-card'
import { SuggestedPackagingCard } from './suggested-packaging-card'
import { HandlingUnitCard } from './handling-unit-card'

// Keyboard Shortcuts Helpers
import { keyboardMapping } from '../../../../keyboard-shortcuts'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faBoxFull, faBoxAlt, faTrashAlt } from '@fortawesome/pro-duotone-svg-icons'

import { MenuOptionProps } from './types'

// Scrolls to and focuses on a handling unit input field by handling unit number
const focusHandlingUnitInput = (handlingUnitNumber: number) => {
  const inputId = `handling-unit-${handlingUnitNumber}`
  const element = document.getElementById(inputId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    try {
      ;(element as HTMLElement).focus()
      if ((element as HTMLInputElement).select) {
        ;(element as HTMLInputElement).select()
      }
    } catch (err) {
      // ignore focus errors
    }
  }
}

const MenuOption = ({
  url,
  onClick,
  icon,
  title,
  arrow,
  children,
  node,
  active,
  disabled,
  complete,
  id,
}: MenuOptionProps) => {
  return (
    <li
      id={id}
      ref={node}
      onClick={onClick}
      className={active ? 'active' : disabled ? 'disabled' : complete ? 'complete' : ''}
    >
      <a href={url || '#'}>
        <span className="icon">
          <FontAwesomeIcon icon={icon} />
        </span>
        <span className="text">{title}</span>
        {arrow ? <FontAwesomeIcon icon={faChevronRight} style={{ color: '#595d6e' }} /> : null}
      </a>
      {children}
    </li>
  )
}

export const PrePackOrder = () => <PackOrder prepack />

export const PackOrder = ({ prepack }: any) => {
  let navigate = useNavigate()

  const order = useParams<{ id: string }>().id
  const {
    state: { csrf },
  } = globalState()

  const [updateList, setUpdateList] = useState(false)
  const { response: resp, loaded }: any = useFetch(`/api/order/${order}/pack_details/`, {})
  const suggestedPackaging = loaded ? resp.suggested_packaging : []
  const status = loaded ? resp.status : null

  const defaultHandlingUnit = {
    id: null,
    handling_unit_number: 1,
    total: 1,
    order_items: [],
    packaging_items: [],
  }

  const [newHandlingUnitId, setNewHandlingUnitId] = useState<number | null>(null)
  const [handlingUnits, setHandlingUnits] = useState([defaultHandlingUnit])
  const [handlingUnitsLoaded, setHandlingUnitsLoaded] = useState(false)
  const [packStatus, setPackStatus] = useState({
    fully_packed: false,
    needs_serial_numbers: false,
    serial_number_enabled: false,
    needs_dimensions: false,
    transport_mode: 'parcel' as const,
  })
  const shouldFocusHighest = useRef(false)

  useEffect(() => {
    if (loaded && resp?.handling_units?.length) {
      setHandlingUnits(resp.handling_units)
      shouldFocusHighest.current = true
    }
    if (loaded) {
      if (status >= 160) {
        navigate(`/order/${order}/`)
      }
      if (resp?.pack_status) {
        setPackStatus(resp.pack_status)
      }
      setHandlingUnitsLoaded(true)
    }
  }, [loaded])

  // Focus on highest handling unit after handling units are rendered in DOM
  useEffect(() => {
    // Check if we have real handling units (not just the default placeholder)
    const hasRealHandlingUnits = handlingUnits.length > 0 && handlingUnits[0].id !== null
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (handlingUnitsLoaded && shouldFocusHighest.current && hasRealHandlingUnits) {
      const highestHandlingUnitNumber = Math.max(...handlingUnits.map((s) => s.handling_unit_number))
      // Use setTimeout to ensure DOM has updated after React render
      timeoutId = setTimeout(() => focusHandlingUnitInput(highestHandlingUnitNumber), 100)
      shouldFocusHighest.current = false
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [handlingUnitsLoaded, handlingUnits])

  function deleteHandlingUnit(handlingUnitNumber: any, id: number | string) {
    if (id) {
      useDelete(`/api/shipment/${id}/`, csrf, false, true, true, async (response: Response) => {
        const resp = await response.json()
        if (resp?.pack_status) {
          setPackStatus(resp?.pack_status)
        }
        setHandlingUnits((prev) => prev.filter((s) => s.handling_unit_number !== handlingUnitNumber))
      })
    } else {
      if (handlingUnits.length === 1) {
        notify({ type: 'error', message: 'An order must have at least 1 handling unit' })
      } else {
        setHandlingUnits(handlingUnits.filter((s) => s.handling_unit_number !== handlingUnitNumber))
      }
    }
  }

  const handleAddPackage = async () => {
    const newHandlingUnitNumber = handlingUnits.length + 1
    const resp = await usePost(
      '/api/shipment/pack_item/',
      { pk: order, handling_unit: newHandlingUnitNumber, barcode: '' },
      csrf,
      false
    )
    if (resp && resp.success) {
      // Make sure pack status is updated since page reload does not occur
      setPackStatus((prev) => ({ ...prev, fully_packed: false }))
      // Prepend the newly created handling unit and mark its id for highlighting
      setHandlingUnits((prev) => [
        { ...defaultHandlingUnit, handling_unit_number: newHandlingUnitNumber, id: resp.id },
        ...prev,
      ])
      setNewHandlingUnitId(resp.id)
      // Clear the new id after highlight duration so prop won't persist
      setTimeout(() => setNewHandlingUnitId(null), 2600)
      // Focus on the newly added package after DOM update
      setTimeout(() => focusHandlingUnitInput(newHandlingUnitNumber), 100)
    } else {
      notify({ type: 'error', message: 'There was an issue adding a handling unit' })
    }
  }

  useEffect(() => {
    keyboardMapping['order']['u'].data.handling_unit_count = handlingUnits.length
    keyboardMapping['order']['d'].data.handling_unit_count = handlingUnits.length
  }, [handlingUnits])

  return (
    <>
      <div className="row">
        <div className="col">
          <StatusBar />
        </div>
      </div>
      <div className="row">
        <div className="col-lg-3 col-xl-2">
          <OrderOptions>
            <ul>
              <GlobalStyles.Tooltip data-title="Reverts all packing">
                <MenuOption
                  id="reset-packing"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to reset packing? This will revert all packing.')) {
                      usePost(`/api/order/${order}/reset_packing/`, {}, csrf)
                    }
                  }}
                  icon={faTrashAlt}
                  title={'Reset Packing'}
                />
              </GlobalStyles.Tooltip>
              <GlobalStyles.Tooltip data-title="Adds another Handling Unit">
                <MenuOption
                  id="add-handling-unit"
                  onClick={handleAddPackage}
                  icon={faBoxAlt}
                  title={'Add handling unit'}
                />
              </GlobalStyles.Tooltip>
              <GlobalStyles.Tooltip data-title="Displays order barcode with picking locations">
                <MenuOption url={`/api/order/${order}/picklist/`} icon={faBoxFull} title={'Order Picklist'} />
              </GlobalStyles.Tooltip>
            </ul>
          </OrderOptions>
        </div>
        <div className="col-lg-7 col-xl-8">
          <div className="row">
            <div className="col-xl-12">
              <OrderItemsCard {...{ updateList }} />
              {suggestedPackaging.length ? <SuggestedPackagingCard {...{ suggestedPackaging }} /> : null}
              <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: 25 }}>
                <GlobalStyles.FullPageCard style={{ borderTop: '2px solid #646c9a', marginRight: '2em', height: 2 }} />
                <h4 style={{ position: 'relative', top: '-10px', letterSpacing: 2 }}>HANDLING UNITS</h4>
                <GlobalStyles.FullPageCard style={{ borderTop: '2px solid #646c9a', marginLeft: '2em', height: 2 }} />
              </div>
              {!handlingUnitsLoaded ? (
                <div style={{ position: 'relative', minHeight: '100px' }}>
                  <CardLoading text="Loading Handling Units..." />
                </div>
              ) : (
                handlingUnits.map((s, id) => (
                  // @ts-ignore
                  <HandlingUnitCard
                    {...s}
                    {...{ deleteHandlingUnit, updateList, setUpdateList, setPackStatus }}
                    handlingUnits={handlingUnits}
                    setHandlingUnits={setHandlingUnits}
                    total={handlingUnits.length}
                    id={s.id || `placeholder-${s.handling_unit_number}-${id}`}
                    key={s.id ?? `hu-${s.handling_unit_number}-${id}`}
                    isNew={s.id !== null && newHandlingUnitId !== null && s.id === newHandlingUnitId}
                  />
                ))
              )}
            </div>
          </div>
        </div>
        <div className="col-xl-2">
          {prepack ? (
            <PrePackWorkflow {...packStatus} current={'pre-pack'} />
          ) : (
            <ShippingWorkflow {...packStatus} current={'pack'} />
          )}
        </div>
      </div>
    </>
  )
}
